import React, { useRef, useState, useEffect, useCallback } from 'react';
import { LazyBrush } from 'lazy-brush';
import { useImage } from '../hooks/useImage';
import { templateApi } from '../api';
import { PnidTemplate } from '../types';

type EditorMode = 'pan' | 'eraser';

interface EraserStroke {
  points: number[];
  size: number;
}

interface TemplateEditorProps {
  template: PnidTemplate;
  onSave?: () => void;
  onBack?: () => void;
  onDelete?: () => void;
  isDeleting?: boolean;
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 10;
const SCALE_STEP = 0.25;
const ZOOM_SENSITIVITY = 1.1;
const MIN_ERASER_SIZE = 5;
const MAX_ERASER_SIZE = 50;
const DEFAULT_ERASER_SIZE = 15;

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  template,
  onSave,
  onBack,
  onDelete,
  isDeleting = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cursorCanvasRef = useRef<HTMLCanvasElement>(null);
  const lazyBrushRef = useRef(new LazyBrush({ radius: 0, enabled: true }));
  
  const [dimensions, setDimensions] = useState({ width: 300, height: 200 });
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  
  // Editor state
  const [mode, setMode] = useState<EditorMode>('pan');
  const [eraserSize, setEraserSize] = useState(DEFAULT_ERASER_SIZE);
  const [eraserStrokes, setEraserStrokes] = useState<EraserStroke[]>([]);
  const [currentStroke, setCurrentStroke] = useState<EraserStroke | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasImageChanges, setHasImageChanges] = useState(false);
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Pan state
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  
  // Metadata editing state
  const [showMetadata, setShowMetadata] = useState(false);
  const [editName, setEditName] = useState(template.name);
  const [editCategory, setEditCategory] = useState(template.category || '');
  const [editTags, setEditTags] = useState((template.tags || []).join(', '));
  
  // Track if metadata has changed
  const hasMetadataChanges = 
    editName !== template.name ||
    editCategory !== (template.category || '') ||
    editTags !== (template.tags || []).join(', ');
  
  // Combined changes flag
  const hasChanges = hasImageChanges || hasMetadataChanges;
  
  // Add cache buster for image URL to force reload after save
  const [imageCacheBuster, setImageCacheBuster] = useState(Date.now());
  const imageUrl = `${templateApi.getImageUrl(template.id)}?t=${imageCacheBuster}`;
  const [resolvedImageUrl, setResolvedImageUrl] = useState('');
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    templateApi.resolveImageUrl(imageUrl)
      .then(({ url, revoke }) => {
        if (cancelled) {
          if (revoke) window.URL.revokeObjectURL(url);
          return;
        }
        objectUrl = revoke ? url : null;
        setResolvedImageUrl(url);
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn('Could not load template image:', error);
          setResolvedImageUrl('');
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [imageUrl]);
  const { image, status } = useImage(resolvedImageUrl);

  // Update container dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setDimensions({ width: clientWidth, height: clientHeight });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Update canvas size when dimensions change
  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.width = dimensions.width;
      canvasRef.current.height = dimensions.height;
    }
    if (cursorCanvasRef.current) {
      cursorCanvasRef.current.width = dimensions.width;
      cursorCanvasRef.current.height = dimensions.height;
    }
  }, [dimensions]);

  // Get all strokes including current
  const getAllStrokes = useCallback(() => {
    return currentStroke 
      ? [...eraserStrokes, currentStroke] 
      : eraserStrokes;
  }, [eraserStrokes, currentStroke]);

  // Draw main canvas (image + eraser strokes)
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Save context state
    ctx.save();
    
    // Apply transform (pan + zoom)
    ctx.setTransform(scale, 0, 0, scale, position.x, position.y);
    
    // Draw image if loaded
    if (image) {
      ctx.drawImage(image, 0, 0);
      
      // Clip eraser strokes to image bounds
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, image.width, image.height);
      ctx.clip();
      
      // Apply eraser strokes (paint white over the image)
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = '#FFFFFF';
      ctx.fillStyle = '#FFFFFF';
      
      const allStrokes = getAllStrokes();
      for (const stroke of allStrokes) {
        ctx.lineWidth = stroke.size;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        if (stroke.points.length >= 4) {
          ctx.beginPath();
          ctx.moveTo(stroke.points[0], stroke.points[1]);
          
          for (let i = 2; i < stroke.points.length; i += 2) {
            ctx.lineTo(stroke.points[i], stroke.points[i + 1]);
          }
          ctx.stroke();
        } else if (stroke.points.length >= 2) {
          // Single point - draw a circle
          ctx.beginPath();
          ctx.arc(stroke.points[0], stroke.points[1], stroke.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      
      ctx.restore();
    }
    
    // Restore context state
    ctx.restore();
  }, [image, scale, position, getAllStrokes]);

  // Draw cursor overlay
  const drawCursor = useCallback(() => {
    const canvas = cursorCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Clear cursor canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (mode !== 'eraser' || !cursorPos) return;
    
    // Calculate screen position from image coordinates
    const screenX = cursorPos.x * scale + position.x;
    const screenY = cursorPos.y * scale + position.y;
    const screenRadius = (eraserSize / 2) * scale;
    
    // Draw outer ring (black)
    ctx.beginPath();
    ctx.arc(screenX, screenY, screenRadius, 0, Math.PI * 2);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.stroke();
    
    // Draw inner ring (white)
    ctx.beginPath();
    ctx.arc(screenX, screenY, Math.max(screenRadius - 1, 0), 0, Math.PI * 2);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.stroke();
  }, [mode, cursorPos, eraserSize, scale, position]);

  // Redraw when dependencies change
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  useEffect(() => {
    drawCursor();
  }, [drawCursor]);

  // Fit image to container when loaded
  const fitToScreen = useCallback(() => {
    if (!image || !containerRef.current) return;

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    
    const scaleX = containerWidth / image.width;
    const scaleY = containerHeight / image.height;
    const newScale = Math.min(scaleX, scaleY, 1) * 0.9;

    setScale(newScale);
    setPosition({
      x: (containerWidth - image.width * newScale) / 2,
      y: (containerHeight - image.height * newScale) / 2,
    });
  }, [image]);

  // Auto-fit when image loads
  useEffect(() => {
    if (status === 'loaded') {
      fitToScreen();
    }
  }, [status, fitToScreen]);

  // Clear cursor when switching away from eraser mode
  useEffect(() => {
    if (mode !== 'eraser') {
      setCursorPos(null);
    }
  }, [mode]);

  // Get image coordinates from screen coordinates
  const getImageCoordinates = useCallback((screenX: number, screenY: number) => {
    return {
      x: (screenX - position.x) / scale,
      y: (screenY - position.y) / scale,
    };
  }, [position, scale]);

  // Get mouse position relative to canvas
  const getCanvasMousePos = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  // Handle mouse wheel zoom
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const mousePos = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    const oldScale = scale;
    const mousePointTo = getImageCoordinates(mousePos.x, mousePos.y);

    const direction = e.deltaY > 0 ? -1 : 1;
    const newScale = Math.min(
      Math.max(oldScale * Math.pow(ZOOM_SENSITIVITY, direction), MIN_SCALE),
      MAX_SCALE
    );

    setScale(newScale);
    setPosition({
      x: mousePos.x - mousePointTo.x * newScale,
      y: mousePos.y - mousePointTo.y * newScale,
    });
  }, [scale, getImageCoordinates]);

  // Attach wheel listener with passive: false to allow preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Zoom controls
  const zoomIn = useCallback(() => {
    setScale((s) => Math.min(s + SCALE_STEP, MAX_SCALE));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((s) => Math.max(s - SCALE_STEP, MIN_SCALE));
  }, []);

  // Handle mouse down
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const mousePos = getCanvasMousePos(e);
    if (!mousePos) return;

    if (mode === 'pan') {
      setIsPanning(true);
      setPanStart({ x: e.clientX - position.x, y: e.clientY - position.y });
      return;
    }

    if (mode === 'eraser' && image) {
      const coords = getImageCoordinates(mousePos.x, mousePos.y);
      
      // Update lazy brush
      lazyBrushRef.current.update({ x: coords.x, y: coords.y }, { both: true });
      const brushCoords = lazyBrushRef.current.getBrushCoordinates();

      // Check if eraser can touch the image
      const halfEraser = eraserSize / 2;
      const canTouchImage = 
        brushCoords.x >= -halfEraser && 
        brushCoords.x <= image.width + halfEraser && 
        brushCoords.y >= -halfEraser && 
        brushCoords.y <= image.height + halfEraser;
      
      if (!canTouchImage) {
        return;
      }

      setIsDrawing(true);
      setCurrentStroke({
        points: [brushCoords.x, brushCoords.y],
        size: eraserSize,
      });
      setHasImageChanges(true);
    }
  }, [mode, image, getImageCoordinates, getCanvasMousePos, position, eraserSize]);

  // Handle mouse move
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const mousePos = getCanvasMousePos(e);
    if (!mousePos) return;

    // Handle panning
    if (isPanning && mode === 'pan') {
      setPosition({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
      return;
    }

    const coords = getImageCoordinates(mousePos.x, mousePos.y);
    
    // Always update cursor position when in eraser mode
    if (mode === 'eraser') {
      setCursorPos(coords);
    }

    if (!isDrawing || mode !== 'eraser' || !currentStroke || !image) return;

    // Update lazy brush and get smoothed coordinates
    lazyBrushRef.current.update({ x: coords.x, y: coords.y });
    const brushCoords = lazyBrushRef.current.getBrushCoordinates();

    // Record actual coordinates
    setCurrentStroke(prev => {
      if (!prev) return null;
      return {
        ...prev,
        points: [...prev.points, brushCoords.x, brushCoords.y],
      };
    });
  }, [mode, isPanning, panStart, isDrawing, currentStroke, image, getImageCoordinates, getCanvasMousePos]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (!isDrawing || !currentStroke) return;
    
    setIsDrawing(false);
    if (currentStroke.points.length >= 2) {
      setEraserStrokes(prev => [...prev, currentStroke]);
    }
    setCurrentStroke(null);
  }, [isPanning, isDrawing, currentStroke]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    setCursorPos(null);
    
    if (isPanning) {
      setIsPanning(false);
    }
    
    if (isDrawing && currentStroke) {
      setIsDrawing(false);
      if (currentStroke.points.length >= 2) {
        setEraserStrokes(prev => [...prev, currentStroke]);
      }
      setCurrentStroke(null);
    }
  }, [isPanning, isDrawing, currentStroke]);

  // Export edited image
  const exportImage = useCallback(async (): Promise<Blob> => {
    if (!image) throw new Error('No image loaded');

    // Create offscreen canvas at original image size
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');

    // Draw original image
    ctx.drawImage(image, 0, 0);

    // Apply eraser strokes (paint white over the image)
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = '#FFFFFF';
    ctx.fillStyle = '#FFFFFF';
    
    const allStrokes = getAllStrokes();

    for (const stroke of allStrokes) {
      ctx.lineWidth = stroke.size;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      if (stroke.points.length >= 4) {
        ctx.beginPath();
        ctx.moveTo(stroke.points[0], stroke.points[1]);
        
        for (let i = 2; i < stroke.points.length; i += 2) {
          ctx.lineTo(stroke.points[i], stroke.points[i + 1]);
        }
        ctx.stroke();
      } else if (stroke.points.length >= 2) {
        // Single point - draw a circle
        ctx.beginPath();
        ctx.arc(stroke.points[0], stroke.points[1], stroke.size / 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to export image'));
        }
      }, 'image/png');
    });
  }, [image, getAllStrokes]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!hasChanges) return;

    setIsSaving(true);
    try {
      // Save image changes if any
      if (hasImageChanges) {
        const imageBlob = await exportImage();
        await templateApi.updateImage(template.id, imageBlob);
        
        // Reset image editor state
        setEraserStrokes([]);
        setCurrentStroke(null);
        setHasImageChanges(false);
        
        // Force image reload
        setImageCacheBuster(Date.now());
      }
      
      // Save metadata changes if any
      if (hasMetadataChanges) {
        const tagsArray = editTags
          .split(',')
          .map(t => t.trim())
          .filter(t => t.length > 0);
        
        await templateApi.update(template.id, {
          name: editName,
          category: editCategory || undefined,
          tags: tagsArray.length > 0 ? tagsArray : undefined,
        });
      }
      
      onSave?.();
    } catch (err) {
      console.error('Failed to save template:', err);
    } finally {
      setIsSaving(false);
    }
  }, [hasChanges, hasImageChanges, hasMetadataChanges, exportImage, template.id, editName, editCategory, editTags, onSave]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    // Reset image editor state
    setEraserStrokes([]);
    setCurrentStroke(null);
    setHasImageChanges(false);
    
    // Reset metadata to original values
    setEditName(template.name);
    setEditCategory(template.category || '');
    setEditTags((template.tags || []).join(', '));
    
    onBack?.();
  }, [onBack, template.name, template.category, template.tags]);

  // Handle delete
  const handleDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    onDelete?.();
  }, [onDelete]);

  // Get cursor style
  const getCursorStyle = () => {
    if (mode === 'eraser') return 'none';
    if (isPanning) return 'grabbing';
    if (mode === 'pan') return 'grab';
    return 'default';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 8,
        borderBottom: '1px solid #e5e7eb',
      }}>
        <button
          onClick={onBack}
          style={{
            padding: '4px 8px',
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            fontSize: 14,
          }}
          title="Back to list"
        >
          ←
        </button>
        <span style={{
          flex: 1,
          fontSize: 14,
          fontWeight: 500,
          color: '#111827',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {editName || template.name}
        </span>
      </div>

      {/* Toolbar */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        padding: 8,
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: 'rgba(243, 244, 246, 0.3)',
      }}>
        {/* Mode toggle */}
        <button
          onClick={() => setMode('pan')}
          style={{
            padding: '4px 8px',
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: mode === 'pan' ? '#3b82f6' : '#ffffff',
            color: mode === 'pan' ? '#ffffff' : '#374151',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 500,
          }}
          title="Pan mode"
        >
          ✋ Pan
        </button>
        <button
          onClick={() => setMode('eraser')}
          style={{
            padding: '4px 8px',
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: mode === 'eraser' ? '#3b82f6' : '#ffffff',
            color: mode === 'eraser' ? '#ffffff' : '#374151',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 500,
          }}
          title="Eraser mode"
        >
          🧹 Eraser
        </button>
        
        <div style={{ width: 1, height: 20, backgroundColor: '#e5e7eb' }} />
        
        {/* Zoom controls */}
        <button
          onClick={zoomOut}
          style={{
            padding: '4px 8px',
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            fontSize: 12,
          }}
          title="Zoom out"
        >
          −
        </button>
        <span style={{
          fontSize: 11,
          fontFamily: 'monospace',
          minWidth: 48,
          textAlign: 'center',
          color: '#6b7280',
        }}>
          {Math.round(scale * 100)}%
        </span>
        <button
          onClick={zoomIn}
          style={{
            padding: '4px 8px',
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            fontSize: 12,
          }}
          title="Zoom in"
        >
          +
        </button>
        <button
          onClick={fitToScreen}
          style={{
            padding: '4px 8px',
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            fontSize: 12,
          }}
          title="Fit to view"
        >
          ⊡
        </button>
      </div>

      {/* Eraser size slider (when eraser mode active) */}
      {mode === 'eraser' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 12px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: 'rgba(243, 244, 246, 0.2)',
        }}>
          <span style={{ fontSize: 11, color: '#6b7280' }}>Size:</span>
          <input
            type="range"
            min={MIN_ERASER_SIZE}
            max={MAX_ERASER_SIZE}
            value={eraserSize}
            onChange={(e) => setEraserSize(Number(e.target.value))}
            style={{ flex: 1, height: 4 }}
          />
          <span style={{
            fontSize: 11,
            fontFamily: 'monospace',
            minWidth: 32,
            textAlign: 'right',
            color: '#6b7280',
          }}>
            {eraserSize}px
          </span>
        </div>
      )}

      {/* Canvas area */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflow: 'hidden',
          background: 'repeating-conic-gradient(#e5e5e5 0% 25%, #fff 0% 50%) 50% / 16px 16px',
          position: 'relative',
          cursor: getCursorStyle(),
          minHeight: 0,
        }}
      >
        {status === 'loading' && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 10,
          }}>
            <span style={{ fontSize: 14, color: '#6b7280' }}>Loading...</span>
          </div>
        )}

        {/* Main canvas for image + eraser strokes */}
        <canvas
          ref={canvasRef}
          style={{ position: 'absolute', inset: 0 }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        />
        
        {/* Cursor overlay canvas */}
        <canvas
          ref={cursorCanvasRef}
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        />
      </div>

      {/* Metadata editing section */}
      <div style={{ borderTop: '1px solid #e5e7eb' }}>
        {/* Collapsible header */}
        <button
          onClick={() => setShowMetadata(!showMetadata)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            border: 'none',
            backgroundColor: showMetadata ? 'rgba(243, 244, 246, 0.5)' : 'transparent',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            color: '#374151',
            textAlign: 'left',
          }}
        >
          <span style={{ 
            transform: showMetadata ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}>
            ▶
          </span>
          <span>Edit Metadata</span>
          {hasMetadataChanges && (
            <span style={{
              marginLeft: 'auto',
              fontSize: 10,
              padding: '2px 6px',
              borderRadius: 4,
              backgroundColor: '#fef3c7',
              color: '#d97706',
            }}>
              Modified
            </span>
          )}
        </button>
        
        {/* Collapsible content */}
        {showMetadata && (
          <div style={{ padding: '8px 12px', backgroundColor: 'rgba(243, 244, 246, 0.3)' }}>
            {/* Name field */}
            <div style={{ marginBottom: 10 }}>
              <label style={{
                display: 'block',
                fontSize: 11,
                fontWeight: 600,
                color: '#6b7280',
                marginBottom: 4,
              }}>
                Name
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                  fontSize: 13,
                  color: '#374151',
                  boxSizing: 'border-box',
                }}
                placeholder="Template name"
              />
            </div>
            
            {/* Category field */}
            <div style={{ marginBottom: 10 }}>
              <label style={{
                display: 'block',
                fontSize: 11,
                fontWeight: 600,
                color: '#6b7280',
                marginBottom: 4,
              }}>
                Category
              </label>
              <input
                type="text"
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                  fontSize: 13,
                  color: '#374151',
                  boxSizing: 'border-box',
                }}
                placeholder="e.g., valve, pump, sensor"
              />
            </div>
            
            {/* Tags field */}
            <div style={{ marginBottom: 8 }}>
              <label style={{
                display: 'block',
                fontSize: 11,
                fontWeight: 600,
                color: '#6b7280',
                marginBottom: 4,
              }}>
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={editTags}
                onChange={(e) => setEditTags(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                  fontSize: 13,
                  color: '#374151',
                  boxSizing: 'border-box',
                }}
                placeholder="tag1, tag2, tag3"
              />
            </div>
            
            {/* Read-only info */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              marginTop: 12,
              paddingTop: 8,
              borderTop: '1px solid #e5e7eb',
              fontSize: 11,
              color: '#9ca3af',
            }}>
              <span>Source: {template.source_pnid_no || 'Unknown'}</span>
              <span>DPI: {template.dpi}</span>
              {hasImageChanges && (
                <span style={{ color: '#d97706', fontWeight: 500 }}>Image modified</span>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Status bar - only show when metadata section is collapsed */}
      {!showMetadata && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '6px 12px',
          borderTop: '1px solid #e5e7eb',
          fontSize: 11,
          color: '#6b7280',
        }}>
          <span>Source: {template.source_pnid_no || 'Unknown'}</span>
          <span>DPI: {template.dpi}</span>
          {hasChanges && (
            <span style={{ color: '#d97706', fontWeight: 500 }}>Unsaved changes</span>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{
        display: 'flex',
        gap: 8,
        padding: 12,
        borderTop: '1px solid #e5e7eb',
      }}>
        {showDeleteConfirm ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            <div style={{ fontSize: 12, color: '#dc2626' }}>
              Are you sure you want to delete this template?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: 6,
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting || isSaving}
              style={{
                padding: '8px 12px',
                border: 'none',
                borderRadius: 6,
                backgroundColor: '#fee2e2',
                color: '#dc2626',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
              title="Delete template"
            >
              🗑️
            </button>
            
            <div style={{ flex: 1 }} />
            
            <button
              onClick={handleCancel}
              disabled={isSaving}
              style={{
                padding: '8px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                backgroundColor: '#ffffff',
                color: '#374151',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              style={{
                padding: '8px 12px',
                border: 'none',
                borderRadius: 6,
                backgroundColor: hasChanges ? '#3b82f6' : '#e5e7eb',
                color: hasChanges ? '#ffffff' : '#9ca3af',
                fontSize: 12,
                fontWeight: 600,
                cursor: hasChanges && !isSaving ? 'pointer' : 'not-allowed',
              }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
